import { z } from 'zod';
import { Id } from '../primitives.js';
import { Fdi } from './dental-chart.js';
import { Kopecks } from './finance.js';
import { FileStatus } from './files.js';

/**
 * Контракты наряда в зуботехническую лабораторию.
 *
 * Наряд — единственная сущность с двумя равноправными арендаторами: строка
 * видна и клинике, и лаборатории, но одно поле — `labCost`, себестоимость —
 * видит только лаборатория. Поэтому оно объявлено `optional`, а не
 * `nullable`: клиника получает ответ, в котором этого ключа нет вовсе, а не
 * `null` — отсутствие ключа не намекает, что скрывать было что-то конкретное.
 *
 * Единицы наряда (`units`) приезжают и уходят вместе с нарядом целиком, как
 * позиции плана лечения: это не отдельная сущность протокола синхронизации.
 *
 * Наряд без партнёрства — не отдельная ветка данных, а тот же наряд с
 * `labTenantId`, равным арендатору самой клиники: у наряда нет второй строны,
 * которая могла бы его увидеть или принять, а вся модель — RLS, чат,
 * назначение техника, автомат статусов — остаётся прежней и работает без
 * единого условия на этот случай. Единственное отличие — `labName`: имя
 * лаборатории от руки, для бумаги, которую увозят курьером.
 *
 * Документация модуля: docs/03-modules/06-lab-orders.md
 */

export const LabOrderStatus = z.enum([
  'created',
  'accepted_by_lab',
  'in_progress',
  'try_in_required',
  'remake',
  'ready',
  'received_by_clinic',
  'installed',
  'closed',
  'cancelled',
]);
export type LabOrderStatus = z.infer<typeof LabOrderStatus>;

export const FaultParty = z.enum(['clinic', 'lab', 'agreed_change']);
export type FaultParty = z.infer<typeof FaultParty>;

export const RestorationType = z.enum(['crown', 'bridge', 'inlay', 'denture', 'braces']);
export type RestorationType = z.infer<typeof RestorationType>;

export const ShadeSystem = z.enum(['vita_classical', 'vita_3d_master']);
export type ShadeSystem = z.infer<typeof ShadeSystem>;

export const UnitRole = z.enum(['abutment', 'pontic', 'crown', 'implant_crown']);
export type UnitRole = z.infer<typeof UnitRole>;

/** Единица работы наряда: конкретный зуб и его роль в конструкции. */
export const LabOrderUnit = z.object({
  id: Id,
  fdi: Fdi,
  role: UnitRole,
});
export type LabOrderUnit = z.infer<typeof LabOrderUnit>;

export const LabOrderUnitInput = z.object({
  fdi: Fdi,
  role: UnitRole,
});
export type LabOrderUnitInput = z.infer<typeof LabOrderUnitInput>;

/**
 * Позиция прейскуранта лаборатории.
 *
 * Отдельный перечень, а не раздел прайса клиники: в прайсе клиники лежит цена
 * для пациента, а здесь — цена работы, из которой собирается наряд. Одна
 * строка на вид работы («Коронка из диоксида циркония»), цена — за единицу.
 *
 * Вид конструкции и материал остаются в схеме ради строк, заведённых до того,
 * как их убрали из формы, но перечнем больше не задаются: в наряде они
 * относятся к конкретному зубу конкретного пациента, а не к строке прейскуранта,
 * и одна и та же работа делается из разных материалов.
 */
export const LabService = z.object({
  id: Id,
  tenantId: Id,
  /**
   * Лаборатория, заведённая клиникой вручную, чей это прайс. `null` — прайс
   * самой лаборатории-арендатора (ведёт `lab_owner`/`lab_manager`): клиника
   * видит его через партнёрство, только при связи и только для чтения.
   * Значение по умолчанию — ради строк, лежащих в кэше с тех времён, когда
   * поля не было.
   */
  laboratoryId: Id.nullable().default(null),
  code: z.string().min(1).max(50),
  name: z.string().min(1).max(300),
  restorationType: RestorationType.nullable(),
  materialCode: z.string().max(100).nullable(),
  /** Цена за одну единицу работы в копейках. */
  price: Kopecks,
  /**
   * Позиция снята с прейскуранта. Не удалена: она стоит в нарядах прошлого
   * года, и удаление разрушило бы их.
   */
  isActive: z.boolean().default(true),
  rowVersion: z.number().int().nonnegative(),
});
export type LabService = z.infer<typeof LabService>;

export const CreateLabServiceRequest = z.object({
  /** Прайс лаборатории, заведённой вручную. Без него — прайс самого арендатора-лаборатории. */
  laboratoryId: Id.optional(),
  code: z.string().trim().min(1).max(50),
  name: z.string().trim().min(1).max(300),
  restorationType: RestorationType.nullable().optional(),
  materialCode: z.string().trim().max(100).nullable().optional(),
  price: Kopecks,
});
export type CreateLabServiceRequest = z.infer<typeof CreateLabServiceRequest>;

export const UpdateLabServiceRequest = z
  .object({
    name: z.string().trim().min(1).max(300).optional(),
    restorationType: RestorationType.nullable().optional(),
    materialCode: z.string().trim().max(100).nullable().optional(),
    price: Kopecks.optional(),
    isActive: z.boolean().optional(),
  })
  .refine((patch) => Object.values(patch).some((value) => value !== undefined), {
    message: 'Изменение позиции прейскуранта пустое',
  });
export type UpdateLabServiceRequest = z.infer<typeof UpdateLabServiceRequest>;

export const LabServices = z.object({ services: z.array(LabService) });
export type LabServices = z.infer<typeof LabServices>;

/**
 * Лаборатория, заведённая клиникой вручную.
 *
 * Не арендатор и не партнёрство: у неё нет своей учётной записи в системе,
 * наряды к ней клиника печатает на бумагу и увозит курьером. Зато у неё есть
 * карточка — контакты, адрес, свой прейскурант — и наряды к ней собираются в
 * взаиморасчёты так же, как к партнёрской. Карточку правит сама клиника;
 * карточка партнёрской лаборатории (`LabPartnership`) — только для чтения,
 * её ведёт другая сторона.
 */
export const Laboratory = z.object({
  id: Id,
  tenantId: Id,
  name: z.string().min(1).max(200),
  contactPerson: z.string().max(200).nullable(),
  phone: z.string().max(32).nullable(),
  email: z.string().max(200).nullable(),
  address: z.string().max(500).nullable(),
  notes: z.string().max(2000).nullable(),
  /** Снята с учёта, а не удалена: она стоит в нарядах прошлого года. */
  isActive: z.boolean().default(true),
  rowVersion: z.number().int().nonnegative(),
});
export type Laboratory = z.infer<typeof Laboratory>;

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .optional();

export const CreateLaboratoryRequest = z.object({
  name: z.string().trim().min(1).max(200),
  contactPerson: optionalText(200),
  phone: optionalText(32),
  email: optionalText(200),
  address: optionalText(500),
  notes: optionalText(2000),
});
export type CreateLaboratoryRequest = z.infer<typeof CreateLaboratoryRequest>;

export const UpdateLaboratoryRequest = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    contactPerson: optionalText(200),
    phone: optionalText(32),
    email: optionalText(200),
    address: optionalText(500),
    notes: optionalText(2000),
    isActive: z.boolean().optional(),
  })
  .refine((patch) => Object.values(patch).some((value) => value !== undefined), {
    message: 'Изменение карточки лаборатории пустое',
  });
export type UpdateLaboratoryRequest = z.infer<typeof UpdateLaboratoryRequest>;

export const Laboratories = z.object({ laboratories: z.array(Laboratory) });
export type Laboratories = z.infer<typeof Laboratories>;

/**
 * Позиция наряда: что заказано, по какой цене за единицу и сколько единиц.
 *
 * Цена копируется в позицию, а не берётся из прейскуранта по ссылке: наряд,
 * заведённый в августе, обязан считаться по августовской цене и через год.
 * `serviceId` остаётся ссылкой на строку прейскуранта — но только чтобы
 * показать, откуда позиция взялась.
 *
 * Позиции приезжают и уходят вместе с нарядом целиком, как единицы работы:
 * это не отдельная сущность протокола синхронизации.
 */
export const LabOrderItem = z.object({
  id: Id,
  serviceId: Id.nullable(),
  name: z.string(),
  unitPrice: Kopecks,
  quantity: z.number().int().positive().max(64),
  /** Цена за единицу, умноженная на количество. Считает сервер. */
  amount: Kopecks,
});
export type LabOrderItem = z.infer<typeof LabOrderItem>;

export const LabOrderItemInput = z.object({
  serviceId: Id.optional(),
  name: z.string().trim().min(1).max(300),
  unitPrice: Kopecks,
  quantity: z.number().int().positive().max(64),
});
export type LabOrderItemInput = z.infer<typeof LabOrderItemInput>;

/** Строка истории переходов: append-only, видна обеим сторонам целиком. */
export const LabOrderStatusChange = z.object({
  id: Id,
  orderId: Id,
  fromStatus: LabOrderStatus.nullable(),
  toStatus: LabOrderStatus,
  changedBy: Id,
  changedAt: z.iso.datetime(),
  reason: z.string().nullable(),
  faultParty: FaultParty.nullable(),
  clientCreatedAt: z.iso.datetime(),
});
export type LabOrderStatusChange = z.infer<typeof LabOrderStatusChange>;

export const LabOrder = z.object({
  id: Id,
  clinicTenantId: Id,
  labTenantId: Id,
  /**
   * Название лаборатории от руки — только у наряда без партнёрства: сервер
   * сам заводит его на арендатора клиники (см. `CreateLabOrderRequest`), а
   * узнать по одному `labTenantId`, что это не настоящая лаборатория, а
   * заглушка, снаружи нечем. `null` здесь означает «наряд с подтверждённым
   * партнёрством», а не «имя неизвестно».
   */
  labName: z.string().max(200).nullable(),
  /**
   * Лаборатория, заведённая клиникой вручную (`Laboratory`), — у наряда к
   * ней. `labName` при этом тоже заполнен — снимком названия на момент
   * заведения, для бумаги. Значение по умолчанию — ради нарядов в кэше,
   * заведённых до появления справочника.
   */
  laboratoryId: Id.nullable().default(null),
  /** Номер наряда: присваивает сервер, сквозной в пределах клиники. */
  number: z.string(),
  /**
   * Карта пациента и лечащий врач. Пусто только у наряда лаборатории от
   * «бумажной» клиники — заказчика без кабинета в Odonix, заведённого
   * лабораторией карточкой: тогда пациент и врач вписаны текстом ниже, как на
   * бумажном наряде. У наряда клиники оба заполнены всегда.
   */
  patientId: Id.nullable(),
  doctorId: Id.nullable(),
  patientName: z.string().max(200).nullable().default(null),
  doctorName: z.string().max(200).nullable().default(null),
  technicianId: Id.nullable(),
  status: LabOrderStatus,
  restorationType: RestorationType,
  materialCode: z.string(),
  shadeSystem: ShadeSystem,
  shadeCode: z.string(),
  dueDate: z.iso.date(),
  /** Срок, подтверждённый лабораторией при приёмке. Переделка его не меняет. */
  originalDueDate: z.iso.date(),
  remakeCount: z.number().int().nonnegative(),
  clinicPrice: Kopecks,
  /** Присутствует только в ответе лаборатории — см. заголовок файла. */
  labCost: Kopecks.optional(),
  notes: z.string().nullable(),
  warrantyMonths: z.number().int().min(0).max(600),
  units: z.array(LabOrderUnit),
  /**
   * Пусто у нарядов, заведённых до появления прейскуранта: там вся сумма
   * лежит в `clinicPrice` без разбивки. Значение по умолчанию, а не
   * обязательное поле, — иначе такой наряд перестал бы читаться с устройства,
   * где он уже лежит в кэше.
   */
  items: z.array(LabOrderItem).default([]),
  createdBy: Id,
  clientCreatedAt: z.iso.datetime(),
  createdAt: z.iso.datetime(),
  rowVersion: z.number().int().nonnegative(),
});
export type LabOrder = z.infer<typeof LabOrder>;

/**
 * Создание наряда. Обязательны лаборатория, врач, конструкция, срок и сумма
 * (позициями прайса или одним числом). Материал, цвет и зубы — по желанию:
 * раньше контракт требовал их всегда, исходя из того, что их отсутствие
 * порождает уточняющие звонки в лабораторию. Но у съёмного протеза нет цвета
 * по шкале, у капы — материала из справочника, а у ремонта — конкретного
 * зуба; принуждение вписывать «—» в обязательное поле давало не данные, а
 * мусор в них. Пустая строка и пустой список означают «не указано» и
 * показываются тире; заполнить их обязана лаборатория при приёмке или врач в
 * комментарии, если для этой работы они нужны.
 *
 * Лаборатория — из подтверждённого партнёрства (`labTenantId`), из
 * справочника заведённых вручную (`laboratoryId`) или вписана от руки
 * (`labName`), когда наряд просто печатают на бумагу. Достаточно одного поля
 * — сервер сам решает, что это за наряд, по тому, какое из них пришло.
 */
export const CreateLabOrderRequest = z
  .object({
    /** Карта пациента и врач — у наряда клиники обязательны. */
    patientId: Id.optional(),
    doctorId: Id.optional(),
    /**
     * Пациент и врач текстом — только у наряда лаборатории от «бумажной»
     * клиники (`laboratoryId` — карточка заказчика). Клиника со своей картой
     * пациента их не передаёт.
     */
    patientName: z.string().trim().min(1).max(200).optional(),
    doctorName: z.string().trim().max(200).optional(),
    /** Лаборатория из подтверждённого партнёрства клиники. */
    labTenantId: Id.optional(),
    /** Лаборатория, заведённая клиникой вручную (`Laboratory`), или карточка клиники у лаборатории. */
    laboratoryId: Id.optional(),
    /** Название лаборатории для бумаги — если нет ни партнёрства, ни карточки. */
    labName: z.string().trim().min(1).max(200).optional(),
    restorationType: RestorationType,
    /** Пусто — материал не указан. */
    materialCode: z.string().trim().max(100).default(''),
    shadeSystem: ShadeSystem.default('vita_classical'),
    /** Пусто — цвет не указан; шкала при этом не имеет значения. */
    shadeCode: z.string().trim().max(50).default(''),
    /** Срок, о котором просит клиника. Лаборатория подтвердит его при приёмке. */
    dueDate: z.iso.date(),
    /**
     * Сумма наряда одной строкой — только когда позиций нет. При заказе по
     * прейскуранту её считает сервер из позиций и присланное значение
     * игнорирует: две суммы на одном наряде разошлись бы на первой же правке
     * количества.
     */
    clinicPrice: Kopecks.optional(),
    notes: z.string().max(2000).optional(),
    warrantyMonths: z.number().int().min(0).max(600).default(0),
    /**
     * Зубы конструкции. Пусто — наряд без привязки к зубам (съёмный протез,
     * капа, ремонт): установка тогда отмечается без записи в паспорт зуба.
     */
    units: z.array(LabOrderUnitInput).max(32).default([]),
    /** Позиции прейскуранта. Пусто — наряд одной суммой, как раньше. */
    items: z.array(LabOrderItemInput).max(64).default([]),
  })
  .refine(
    (request) => request.labTenantId !== undefined || request.laboratoryId !== undefined || request.labName !== undefined,
    {
      message: 'Выберите лабораторию из списка или впишите её название для бумажного наряда',
      path: ['labName'],
    },
  )
  .refine((request) => request.items.length > 0 || request.clinicPrice !== undefined, {
    message: 'Добавьте работы из прейскуранта или укажите сумму наряда',
    path: ['items'],
  })
  .refine((request) => (request.patientId !== undefined && request.doctorId !== undefined) || request.patientName !== undefined, {
    message: 'Укажите пациента: картой и врачом — у клиники, текстом — у наряда от бумажной клиники',
    path: ['patientId'],
  });
export type CreateLabOrderRequest = z.infer<typeof CreateLabOrderRequest>;

/**
 * Назначение техника — отдельное действие, не смена статуса: наряд остаётся в
 * `created`, пока лаборатория не примет его отдельным переходом. Так менеджер
 * может назначить исполнителя заранее, не выполняя приёмку в ту же секунду, и
 * так же переназначить исполнителя посреди работы.
 */
export const AssignTechnicianRequest = z.object({
  technicianId: Id,
});
export type AssignTechnicianRequest = z.infer<typeof AssignTechnicianRequest>;

/**
 * Смена статуса — один сценарий на все переходы, включая переделку и отмену.
 * Поля, обязательные для конкретного перехода, проверяет
 * `@odonix/core.labOrders.checkTransition`, а не схема: одно и то же тело
 * годится для лёгкого перехода и для переделки, где не хватит одного `to`.
 */
export const ChangeLabOrderStatusRequest = z.object({
  to: LabOrderStatus,
  /** Обязательна для переделки и отмены. */
  reason: z.string().max(500).optional(),
  /** Обязательна для переделки. */
  faultParty: FaultParty.optional(),
  /** Подтверждённый (при приёмке) или пересчитанный (при переделке) срок. */
  dueDate: z.iso.date().optional(),
  /** Себестоимость лаборатории: выставляется ей самой, обычно при приёмке. */
  labCost: Kopecks.optional(),
  /**
   * Установленные единицы — для перехода в `installed`. Пустой список допустим
   * только у наряда без зубов; у наряда с зубами его отклонит автомат
   * (`checkTransition`), а не схема: схема не знает, есть ли у наряда зубы.
   */
  installedUnitIds: z.array(Id).max(32).optional(),
});
export type ChangeLabOrderStatusRequest = z.infer<typeof ChangeLabOrderStatusRequest>;

/** Изменение наряда в очереди устройства: смена статуса или назначение техника. */
export const LabOrderChange = z.discriminatedUnion('change', [
  ChangeLabOrderStatusRequest.extend({ change: z.literal('status') }),
  AssignTechnicianRequest.extend({ change: z.literal('assign') }),
]);
export type LabOrderChange = z.infer<typeof LabOrderChange>;

export const LabOrdersQuery = z.object({
  status: LabOrderStatus.optional(),
  overdue: z.coerce.boolean().optional(),
  /** Только у лаборатории есть смысл: наряды конкретного техника. */
  technicianId: Id.optional(),
  /** Клиника фильтрует по конкретной лаборатории и наоборот. */
  counterpartyTenantId: Id.optional(),
  /** Наряды к лаборатории, заведённой вручную. */
  laboratoryId: Id.optional(),
});
export type LabOrdersQuery = z.infer<typeof LabOrdersQuery>;

export const LabOrders = z.object({ orders: z.array(LabOrder) });
export type LabOrders = z.infer<typeof LabOrders>;

export const LabOrderHistory = z.object({ changes: z.array(LabOrderStatusChange) });
export type LabOrderHistory = z.infer<typeof LabOrderHistory>;

/**
 * Партнёрство клиники и лаборатории: без него наряды одной стороне другой не
 * видны. Заявку создаёт любая сторона по e-mail владельца другой стороны —
 * тот же принцип, что у приглашения сотрудника, а не «клиника вписывает чужой
 * идентификатор арендатора».
 */
export const LabPartnershipStatus = z.enum([
  'pending_from_clinic',
  'pending_from_lab',
  'active',
  'declined',
  'ended',
]);
export type LabPartnershipStatus = z.infer<typeof LabPartnershipStatus>;

export const LabPartnership = z.object({
  id: Id,
  clinicTenantId: Id,
  labTenantId: Id,
  clinicName: z.string(),
  labName: z.string(),
  status: LabPartnershipStatus,
  requestedBy: Id,
  respondedBy: Id.nullable(),
  /** Сообщение подавшего заявку — своё предложение по работе. Пусто у заявок без текста и у старых строк. */
  message: z.string().max(1000).nullable().default(null),
  createdAt: z.iso.datetime(),
  respondedAt: z.iso.datetime().nullable(),
  rowVersion: z.number().int().nonnegative(),
});
export type LabPartnership = z.infer<typeof LabPartnership>;

/**
 * Заявка на партнёрство: другую сторону называют либо e-mail её владельца
 * (как раньше), либо арендатором с карты клиник и лабораторий — там его
 * знают по карточке, а не по почте. Сообщение — своё предложение по работе;
 * его видит тот, кто отвечает на заявку.
 */
export const RequestPartnershipRequest = z
  .object({
    /** Владелец клиники или лаборатории на другой стороне партнёрства. */
    counterpartyOwnerEmail: z.email().optional(),
    /** Другая сторона — карточкой с карты. */
    counterpartyTenantId: Id.optional(),
    message: z.string().trim().max(1000).optional(),
  })
  .refine((request) => request.counterpartyOwnerEmail !== undefined || request.counterpartyTenantId !== undefined, {
    message: 'Укажите e-mail владельца другой стороны или выберите её на карте',
    path: ['counterpartyOwnerEmail'],
  });
export type RequestPartnershipRequest = z.infer<typeof RequestPartnershipRequest>;

export const RespondPartnershipRequest = z.object({
  decision: z.enum(['accept', 'decline', 'end']),
});
export type RespondPartnershipRequest = z.infer<typeof RespondPartnershipRequest>;

export const LabPartnerships = z.object({ partnerships: z.array(LabPartnership) });
export type LabPartnerships = z.infer<typeof LabPartnerships>;

// --- Вложения к наряду (веха 3.9) ------------------------------------------

/**
 * Вид исходного снимка — подпись на плитке, а не отдельный перечень видов
 * файла в `files`: там вложение наряда — один вид (`lab_attachment`) с
 * общим для всех набором MIME (image/jpeg, image/png, application/pdf).
 */
export const LabAttachmentKind = z.enum(['document_scan', 'intraoral_photo', 'panoramic_xray', 'periapical_xray']);
export type LabAttachmentKind = z.infer<typeof LabAttachmentKind>;

export const LabOrderAttachment = z.object({
  id: Id,
  orderId: Id,
  kind: LabAttachmentKind,
  status: FileStatus,
  uploadedBy: Id,
  createdAt: z.iso.datetime(),
});
export type LabOrderAttachment = z.infer<typeof LabOrderAttachment>;

export const LabOrderAttachments = z.object({ attachments: z.array(LabOrderAttachment) });
export type LabOrderAttachments = z.infer<typeof LabOrderAttachments>;

export const CreateLabOrderAttachmentRequest = z.object({
  kind: LabAttachmentKind,
  contentType: z.string().min(1).max(100),
  sizeBytes: z.number().int().positive(),
  checksumSha256: z.string().regex(/^[a-f0-9]{64}$/, 'Ожидается SHA-256 в шестнадцатеричном виде'),
});
export type CreateLabOrderAttachmentRequest = z.infer<typeof CreateLabOrderAttachmentRequest>;
